import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Target, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  MessageSquare,
  ArrowRight
} from 'lucide-react';
import { formatDate, getStatusColor, getHorizonColor, getDepartmentColor, formatCurrency } from '../lib/utils';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const HORIZONS = ["All", "Weekly", "15-Day", "30-Day", "45-Day", "Monthly", "6-Month", "1-Year"];

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horizonFilter, setHorizonFilter] = useState('All');

  useEffect(() => {
    loadDashboard();
  }, [horizonFilter]);

  const loadDashboard = async () => {
    try {
      const response = isAdmin() 
        ? await dashboardApi.getAdmin(horizonFilter)
        : await dashboardApi.getMember();
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 skeleton rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return isAdmin() ? (
    <AdminDashboard data={data} horizonFilter={horizonFilter} setHorizonFilter={setHorizonFilter} />
  ) : (
    <MemberDashboard data={data} />
  );
}

function MemberDashboard({ data }) {
  const { today_plan, today_outcomes, goals_by_horizon, open_actionables, recent_feedback } = data || {};

  return (
    <div className="space-y-8" data-testid="member-dashboard">
      <div className="page-header">
        <h1 className="page-title">My Dashboard</h1>
        <p className="page-subtitle">Track your daily progress and goals</p>
      </div>

      {/* Today's Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          icon={<Target className="w-5 h-5" />}
          label="Today's Outcomes"
          value={today_outcomes?.length || 0}
          subtext="of 4 max"
          color="blue"
        />
        <StatCard
          icon={<CheckCircle2 className="w-5 h-5" />}
          label="Completed"
          value={today_outcomes?.filter(o => o.status === 'Done').length || 0}
          subtext="outcomes"
          color="green"
        />
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          label="In Progress"
          value={today_outcomes?.filter(o => o.status === 'In Progress').length || 0}
          subtext="outcomes"
          color="amber"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Open Actions"
          value={open_actionables?.length || 0}
          subtext="pending"
          color="red"
        />
      </div>

      {/* Today's Outcomes */}
      <Card className="border border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold">Today's Outcomes</CardTitle>
          <Link to="/daily-plan">
            <Button variant="outline" size="sm" data-testid="go-to-daily-plan">
              Open Daily Plan <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {today_outcomes?.length > 0 ? (
            <div className="space-y-3">
              {today_outcomes.map((outcome, idx) => (
                <div key={outcome.id} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{outcome.statement}</p>
                      <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                        <Badge variant="outline" className={getStatusColor(outcome.status)}>
                          {outcome.status}
                        </Badge>
                        <span>{outcome.metric_type}: {outcome.actual_value}/{outcome.target_value}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-slate-900">
                        {Math.round((outcome.actual_value / outcome.target_value) * 100)}%
                      </div>
                      <Progress 
                        value={(outcome.actual_value / outcome.target_value) * 100} 
                        className="w-24 h-2 mt-1"
                      />
                    </div>
                  </div>
                  {outcome.tasks?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">Tasks: {outcome.tasks.filter(t => t.status === 'Done').length}/{outcome.tasks.length}</p>
                      <div className="flex flex-wrap gap-1">
                        {outcome.tasks.map(task => (
                          <Badge 
                            key={task.id} 
                            variant="outline" 
                            className={task.status === 'Done' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50'}
                          >
                            {task.description}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No outcomes planned for today</p>
              <Link to="/daily-plan">
                <Button variant="outline" size="sm" className="mt-3">
                  Create Daily Plan
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Goals by Horizon */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Goals by Horizon</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="Weekly" className="w-full">
            <TabsList className="grid grid-cols-5 mb-4">
              {['Weekly', '15-Day', '30-Day', '6-Month', '1-Year'].map(horizon => (
                <TabsTrigger key={horizon} value={horizon} className="text-xs">
                  {horizon}
                </TabsTrigger>
              ))}
            </TabsList>
            {['Weekly', '15-Day', '30-Day', '6-Month', '1-Year'].map(horizon => (
              <TabsContent key={horizon} value={horizon}>
                {goals_by_horizon?.[horizon]?.length > 0 ? (
                  <div className="space-y-3">
                    {goals_by_horizon[horizon].map(goal => (
                      <div key={goal.id} className="p-3 bg-slate-50 rounded-lg border">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{goal.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className={getStatusColor(goal.status)}>
                                {goal.status}
                              </Badge>
                              <span className="text-sm text-slate-500">
                                Target: {formatCurrency(goal.target_value, goal.unit)}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold">{Math.round(goal.progress || 0)}%</span>
                            <Progress value={goal.progress || 0} className="w-20 h-2 mt-1" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-6 text-slate-500">No goals for this horizon</p>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Open Actionables & Feedback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Open Actionables
            </CardTitle>
          </CardHeader>
          <CardContent>
            {open_actionables?.length > 0 ? (
              <div className="space-y-2">
                {open_actionables.slice(0, 5).map((action, idx) => (
                  <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="font-medium text-sm">{action.problem}</p>
                    <p className="text-xs text-slate-500 mt-1">Due: {formatDate(action.due_date)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-slate-500">No open actionables</p>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              Recent Feedback
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recent_feedback?.length > 0 ? (
              <div className="space-y-2">
                {recent_feedback.slice(0, 5).map((fb, idx) => (
                  <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="font-medium text-sm">{fb.question}</p>
                    <p className="text-xs text-slate-600 mt-1">{fb.answer}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-slate-500">No recent feedback</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AdminDashboard({ data, horizonFilter, setHorizonFilter }) {
  const { goals_by_horizon, department_overview, leaderboard, open_problems, recent_meetings, team_targets, behind_target } = data || {};

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="page-header">
        <h1 className="page-title">Admin Dashboard</h1>
        <p className="page-subtitle">Team performance overview</p>
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          icon={<Target className="w-5 h-5" />}
          label="Active Goals"
          value={Object.values(goals_by_horizon || {}).reduce((sum, h) => sum + (h.active || 0), 0)}
          subtext="across all horizons"
          color="blue"
        />
        <StatCard
          icon={<CheckCircle2 className="w-5 h-5" />}
          label="Completed"
          value={Object.values(goals_by_horizon || {}).reduce((sum, h) => sum + (h.completed || 0), 0)}
          subtext="goals"
          color="green"
        />
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Team Members"
          value={leaderboard?.length || 0}
          subtext="active"
          color="purple"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Open Problems"
          value={open_problems?.length || 0}
          subtext="need attention"
          color="red"
        />
      </div>

      {/* Goals by Horizon */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Goals by Horizon</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(goals_by_horizon || {}).map(([horizon, stats]) => (
              <div key={horizon} className="p-4 bg-slate-50 rounded-lg border text-center">
                <Badge className={getHorizonColor(horizon)} variant="outline">{horizon}</Badge>
                <div className="mt-3">
                  <p className="text-2xl font-bold">{stats.total || 0}</p>
                  <p className="text-xs text-slate-500">Total Goals</p>
                </div>
                <div className="mt-2 flex justify-center gap-4 text-xs">
                  <span className="text-emerald-600">{stats.active || 0} Active</span>
                  <span className="text-blue-600">{stats.completed || 0} Done</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Department Overview */}
      <Card className="border border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Department Overview</CardTitle>
          <Select value={horizonFilter} onValueChange={setHorizonFilter}>
            <SelectTrigger className="w-40" data-testid="dept-horizon-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HORIZONS.map(h => <SelectItem key={h} value={h}>{h === 'All' ? 'All Horizons' : h}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Active Goals</th>
                  <th>Target</th>
                  <th>Actual</th>
                  <th>Completion</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(department_overview || {}).map(([dept, stats]) => (
                  <tr key={dept}>
                    <td className="font-medium">{dept}</td>
                    <td>{stats.goals_count || 0}</td>
                    <td>{stats.total_target > 0 ? formatCurrency(stats.total_target) : '-'}</td>
                    <td>{stats.total_actual > 0 ? formatCurrency(stats.total_actual) : '-'}</td>
                    <td>
                      {stats.completion_rate !== null && stats.total_target > 0 ? (
                        <div className="flex items-center gap-2">
                          <Progress value={stats.completion_rate} className="w-20 h-2" />
                          <span className="text-sm">{Math.round(stats.completion_rate)}%</span>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard & Problems */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Team Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leaderboard?.slice(0, 5).map((member, idx) => (
                <div key={member.user_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    idx === 0 ? 'bg-amber-100 text-amber-700' :
                    idx === 1 ? 'bg-slate-200 text-slate-700' :
                    idx === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{member.name}</p>
                    <p className="text-xs text-slate-500">
                      {member.total_target > 0 
                        ? `${formatCurrency(member.total_actual)} / ${formatCurrency(member.total_target)}`
                        : `${member.completed_goals}/${member.total_goals} goals`
                      }
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold">
                      {member.total_target > 0 ? Math.round(member.completion_rate) : (member.total_goals > 0 ? Math.round((member.completed_goals / member.total_goals) * 100) : 0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Open Problems
            </CardTitle>
          </CardHeader>
          <CardContent>
            {open_problems?.length > 0 ? (
              <div className="space-y-2">
                {open_problems.slice(0, 5).map((problem, idx) => (
                  <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-100">
                    <p className="font-medium text-sm">{problem.problem}</p>
                    <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                      <span>Due: {formatDate(problem.due_date)}</span>
                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                        {problem.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-slate-500">No open problems</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Behind Target Widget */}
      {behind_target?.length > 0 && (
        <Card className="border border-red-200 bg-red-50/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2 text-red-700">
              <TrendingDown className="w-5 h-5" />
              Behind Target - Goals Needing Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {behind_target.slice(0, 5).map((item) => (
                <Link key={item.goal_id} to={`/goals/${item.goal_id}`} className="block">
                  <div className="p-4 bg-white rounded-lg border border-red-200 hover:border-red-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold">{item.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={getDepartmentColor(item.department)}>
                            {item.department}
                          </Badge>
                          <Badge className={getHorizonColor(item.horizon)}>{item.horizon}</Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xl font-bold text-red-600">
                          -{formatCurrency(item.deficit, item.unit)}
                        </span>
                        <p className="text-xs text-slate-500 mt-1">
                          Expected: {formatCurrency(item.expected, item.unit)} | 
                          Achieved: {formatCurrency(item.achieved, item.unit)}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Meetings */}
      <Card className="border border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Recent Meetings</CardTitle>
          <Link to="/meetings">
            <Button variant="outline" size="sm">View All</Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recent_meetings?.length > 0 ? (
            <div className="space-y-2">
              {recent_meetings.slice(0, 5).map(meeting => (
                <div key={meeting.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div>
                    <p className="font-medium">{meeting.meeting_type}</p>
                    <p className="text-sm text-slate-500">{formatDate(meeting.date)}</p>
                  </div>
                  <Badge variant="outline">{meeting.participants?.length || 0} participants</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-6 text-slate-500">No recent meetings</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon, label, value, subtext, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="stat-card">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
        {icon}
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      <p className="text-xs text-slate-400 mt-1">{subtext}</p>
    </div>
  );
}
